import React, { useState, useEffect, useCallback, useRef } from "react";
import TopNav from "../components/layout/TopNav";
import Sidebar, { SIDEBAR_WIDTH } from "../components/layout/Sidebar";
import TheBrief from "../components/sections/TheBrief";
import PersonalDeveloperSummary from "../components/sections/PersonalDeveloperSummary";
import WorkRequiringAttention from "../components/sections/WorkRequiringAttention";
import InvestigationDrawer from "../components/ticket/InvestigationDrawer";
import TicketChip from "../components/ticket/TicketChip";
import { G, TYPE, FONT_FAMILY, OVERVIEW } from "../constants/theme";
import { API_BASE } from "../constants/api";
import Description from "../components/typography/Description";
import ScoreBreakdown from "../components/common/ScoreBreakdown";
import OverviewSection from "../components/overview/OverviewSection";
import SectionHeader from "../components/overview/SectionHeader";
import SectionNav from "../components/overview/SectionNav";

// In-page navigator targets — order matches the section order on the page.
const OVERVIEW_SECTIONS = [
  { id: "ov-overview", label: "Overview" },
  { id: "ov-snapshot", label: "Snapshot" },
  { id: "ov-priority", label: "Priority Work" },
  { id: "ov-daily", label: "Daily Execution" },
  { id: "ov-threats", label: "Threats to Delivery" },
  { id: "ov-team", label: "Team Benchmarking" },
  { id: "ov-early-warning", label: "Early Warning" },
  { id: "ov-sprint", label: "Sprint Execution" },
  { id: "ov-assistant", label: "AI Assistant" },
];
const ANCHOR = { scrollMarginTop: 120 };


// ─── Commitment Badge ─────────────────────────────────────────────────────────
const STATUS_BADGE = {
  NO_ACTIVITY:      { label: "NO PR",     bg: "rgba(220,38,38,0.1)",   color: "#DC2626" },
  ACTIVE:           { label: "ACTIVE",    bg: "rgba(22,163,74,0.12)",  color: "#16A34A" },
  FULFILLED:        { label: "FULFILLED", bg: "rgba(37,99,235,0.12)",  color: "#2563EB" },
  MULTI_PR_ACTIVITY:{ label: "MULTI-PR",  bg: "rgba(124,58,237,0.12)", color: "#7C3AED" },
};
function CommitmentBadge({ status, loading }) {
  if (loading) {
    return (
      <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 3, background: "rgba(156,163,175,0.1)", color: "#9CA3AF", letterSpacing: "0.05em", flexShrink: 0 }}>
        …
      </span>
    );
  }
  const s = STATUS_BADGE[status];
  if (!s) {
    return (
      <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 3, background: "rgba(107,114,128,0.1)", color: "#6B7280", letterSpacing: "0.05em", flexShrink: 0 }}>
        UNAVAIL
      </span>
    );
  }
  return (
    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 3, background: s.bg, color: s.color, letterSpacing: "0.05em", flexShrink: 0 }}>
      {s.label}
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Dashboard() {
  const [ticketCount, setTicketCount] = useState(0);
  const [priorityTickets, setPriorityTickets] = useState([]);
  const [criticalTickets, setCriticalTickets] = useState([]);
  const [ageingTickets, setAgeingTickets] = useState([]);
  const [ageingCount, setAgeingCount] = useState(0);
  const [developerWorkload, setDeveloperWorkload] = useState([]);
  const [idle30Count, setIdle30Count] = useState(0);
  const [idleTickets, setIdleTickets] = useState([]);
  const [quickWinCount, setQuickWinCount] = useState(0);
  const [quickWinTickets, setQuickWinTickets] = useState([]);
  const [statusSummary, setStatusSummary] = useState(null);
  // ── Developer personalization — ONE shared selector ("" = All Developers) ──
  const [selectedDeveloper, setSelectedDeveloper] = useState("");
  const [personalLoading, setPersonalLoading] = useState(true);
  const [commitView, setCommitView] = useState("morning");
  const [sprintDriver, setSprintDriver] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [todayCommitment, setTodayCommitment] = useState(null);
  const [devTickets, setDevTickets] = useState([]);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [eveningChecklist, setEveningChecklist] = useState({});
  const [commitAnalytics, setCommitAnalytics] = useState(null);
  const [commitLoading, setCommitLoading] = useState(false);
  const [carryForward, setCarryForward] = useState([]);
  const [commitVerification, setCommitVerification] = useState({});
  const [commitTicketSearch, setCommitTicketSearch] = useState("");
  const dateRange = "all";
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [benchmarkSort, setBenchmarkSort] = useState("closed");
  const [escalationData, setEscalationData] = useState(null);
  const [githubOverview, setGithubOverview] = useState(null);
  const [githubDevelopers, setGithubDevelopers] = useState([]);
  const [githubRisk, setGithubRisk] = useState(null);
  const [githubTopRisks, setGithubTopRisks] = useState([]);
  const [devIntelligence, setDevIntelligence] = useState(null);
  const [allDevIntelligence, setAllDevIntelligence] = useState({});
  const [bounceData, setBounceData] = useState(null);
  const [deliveryIntel, setDeliveryIntel] = useState(null);
  const [earlyWarning, setEarlyWarning] = useState(null);
  const [expandedSignal, setExpandedSignal] = useState(null);
  const [expandBounce, setExpandBounce] = useState(false);
  const [expandPRQueue, setExpandPRQueue] = useState(false);
  const [investigationTicket, setInvestigationTicket] = useState(null);
  const [investigationData, setInvestigationData] = useState(null);
  const [investigationPRs, setInvestigationPRs] = useState([]);
  const [investigationLoading, setInvestigationLoading] = useState(false);

  const investigationCache = useRef({});

  const openInvestigation = useCallback((ticketKey) => {
    if (!ticketKey) return;
    setInvestigationTicket(ticketKey);
    const cached = investigationCache.current[ticketKey];
    if (cached) {
      setInvestigationData(cached.lifecycle);
      setInvestigationPRs(cached.prs);
      setInvestigationLoading(false);
      return;
    }
    setInvestigationData(null);
    setInvestigationPRs([]);
    setInvestigationLoading(true);
    Promise.all([
      fetch(`${API_BASE}/lifecycle/ticket/${ticketKey}`).then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); }),
      fetch(`${API_BASE}/github/linking/ticket/${ticketKey}`).then(r => r.json()).catch(() => ({ linkedPRs: [] })),
    ])
      .then(([lifecycle, prResult]) => {
        const prs = prResult.linkedPRs || [];
        investigationCache.current[ticketKey] = { lifecycle, prs };
        setInvestigationData(lifecycle);
        setInvestigationPRs(prs);
      })
      .catch(() => {
        setInvestigationData(null);
        setInvestigationPRs([]);
      })
      .finally(() => setInvestigationLoading(false));
  }, []);

const fetchAllData = useCallback((range, developer) => {
  // Team-wide query string (range only) — used by developer-workload / escalation.
  const teamParams = new URLSearchParams();
  if (range && range !== "all") teamParams.set("range", range);
  const qs = teamParams.toString() ? `?${teamParams}` : "";

  // Personal query string — adds the optional assignee filter (server filters
  // BEFORE scoring/sorting/slicing). URLSearchParams handles encoding.
  const personalParams = new URLSearchParams(teamParams);
  if (developer) personalParams.set("assignee", developer);
  const pqs = personalParams.toString() ? `?${personalParams}` : "";

  fetch(`${API_BASE}/tickets/count${pqs}`)
    .then((res) => res.json())
    .then((data) => { setTicketCount(data.count); })
    .catch(() => {})
    .finally(() => setPersonalLoading(false));

  fetch(`${API_BASE}/tickets/priority${pqs}`)
    .then((res) => res.json())
    .then((data) => { setPriorityTickets(data); });

  fetch(`${API_BASE}/tickets/critical${pqs}`)
    .then((res) => res.json())
    .then((data) => {
      setCriticalTickets(
        data.map((ticket) => ({
          ticket: ticket.ticketNo,
          detail: `Priority: ${ticket.priority}`,
          priority: ticket.priority,
          status: ticket.status,
          assignedTo: ticket.assignedTo,
          ageingDays: ticket.ageingDays,
          raisedOn: ticket.raisedOn,
          module: ticket.module,
          prStatus: ticket.prStatus,
          riskScore: ticket.riskScore,
          reasons: ticket.reasons,
          actions: ticket.actions,
        }))
      );
    });

  fetch(`${API_BASE}/tickets/ageing${pqs}`)
    .then((res) => res.json())
    .then((data) => {
      setAgeingCount(data.count);
      setAgeingTickets(
        data.tickets.map((ticket) => ({
          ticket: ticket.ticketNo,
          detail: `${ticket.ageingDays} days old`,
          ageingDays: ticket.ageingDays,
          createdDate: ticket.raisedOn,
          assignedTo: ticket.assignedTo,
          status: ticket.status,
          slaTarget: ticket.slaTarget,
          overdueDays: ticket.overdueDays,
          breachDate: ticket.breachDate,
          riskLevel: ticket.riskLevel,
          recommendedAction: ticket.recommendedAction,
        }))
      );
    });

  fetch(`${API_BASE}/tickets/quick-wins${pqs}`)
    .then((res) => res.json())
    .then((data) => {
      setQuickWinCount(data.count);
      setQuickWinTickets(
        data.tickets.map((ticket) => ({
          ticket: ticket.ticketNo,
          detail: ticket.status,
          priority: ticket.priority,
          ageingDays: ticket.ageingDays,
          assignedTo: ticket.assignedTo,
          winScore: ticket.winScore,
          scoreBreakdown: ticket.scoreBreakdown,
          reason: ticket.reason,
          action: ticket.action,
        }))
      );
    })
    .catch(console.error);

  fetch(`${API_BASE}/tickets/idle-summary${pqs}`)
    .then((res) => res.json())
    .then((data) => {
      setIdle30Count(data.idle30Count);
      setIdleTickets(data.tickets);
    })
    .catch(console.error);

  // Personal Developer Summary — raw per-status counts (assignee applied server-side).
  fetch(`${API_BASE}/tickets/status-summary${pqs}`)
    .then((res) => res.json())
    .then((data) => { setStatusSummary(data); })
    .catch(() => {});

  // Team-wide — NEVER personalized (selector source + team escalation data).
  fetch(`${API_BASE}/tickets/developer-workload${qs}`)
    .then((res) => res.json())
    .then((data) => { setDeveloperWorkload(data); })
    .catch(console.error);

  fetch(`${API_BASE}/tickets/escalation-center`)
    .then((res) => res.json())
    .then((data) => { setEscalationData(data); })
    .catch(console.error);
}, []);

useEffect(() => {
  // Clear the previous developer's personal data so it can never remain visible
  // while the newly selected developer's data loads.
  setPersonalLoading(true);
  setTicketCount(0);
  setPriorityTickets([]);
  setCriticalTickets([]);
  setAgeingCount(0);
  setAgeingTickets([]);
  setQuickWinCount(0);
  setQuickWinTickets([]);
  setIdle30Count(0);
  setIdleTickets([]);
  setStatusSummary(null);
  fetchAllData(dateRange, selectedDeveloper);
}, [dateRange, selectedDeveloper, fetchAllData]);

const fetchGitHubData = useCallback(async () => {
  try {
    const [ov, dev, br, pr] = await Promise.all([
      fetch(`${API_BASE}/github/analytics/overview`).then(r => r.json()),
      fetch(`${API_BASE}/github/analytics/developers`).then(r => r.json()),
      fetch(`${API_BASE}/github/analytics/branches`).then(r => r.json()),
      fetch(`${API_BASE}/github/analytics/pull-requests`).then(r => r.json()),
    ]);
    if (ov?.enabled !== false && ov?.ok !== false) {
      setGithubOverview(ov);
    }
    if (dev?.enabled !== false) setGithubDevelopers(dev?.developers || []);
    if (br?.enabled !== false || pr?.enabled !== false) setGithubRisk({ branches: br, prs: pr });
  } catch {}
}, []);

useEffect(() => { fetchGitHubData(); }, [fetchGitHubData]);

useEffect(() => {
  fetch(`${API_BASE}/github/developers/intelligence`)
    .then(r => r.json())
    .then(d => {
      if (Array.isArray(d)) {
        const map = {};
        d.forEach(p => { map[p.developer] = p; });
        setAllDevIntelligence(map);
      }
    })
    .catch(() => {});
}, []);

useEffect(() => {
  fetch(`${API_BASE}/github/risk/top?limit=5`)
    .then(r => r.json())
    .then(d => setGithubTopRisks(Array.isArray(d) ? d : []))
    .catch(() => {});
}, []);

useEffect(() => {
  // Workflow Bounce is personalized: pass the selected developer through to the
  // backend so the bounce set + rate are computed over that developer's tickets.
  setBounceData(null);
  setExpandBounce(false);
  const p = selectedDeveloper ? `?assignee=${encodeURIComponent(selectedDeveloper)}` : "";
  fetch(`${API_BASE}/lifecycle/bounce${p}`)
    .then(r => r.json())
    .then(data => { if (data && !data.error) setBounceData(data); })
    .catch(() => {});
}, [selectedDeveloper]);

useEffect(() => {
  fetch(`${API_BASE}/delivery/intelligence`)
    .then(r => r.json())
    .then(data => { if (!data.error) setDeliveryIntel(data); })
    .catch(() => {});
}, []);

useEffect(() => {
  fetch(`${API_BASE}/early-warning/signals`)
    .then(r => r.json())
    .then(data => { if (!data.error) setEarlyWarning(data); })
    .catch(() => {});
}, []);

const fetchBenchmark = useCallback((dev, sort) => {
  // Always fetched — the response's comparisonTable is the GLOBAL team table.
  // An empty `developer` returns the full table with selectedDeveloper=null.
  const params = new URLSearchParams({ developer: dev || "", sort: sort || "closed" });
  fetch(`${API_BASE}/tickets/team-benchmark?${params}`)
    .then(r => r.json())
    .then(data => setBenchmarkData(data))
    .catch(() => {});
}, []);

const fetchTodayCommitment = useCallback((dev) => {
  if (!dev) return;
  fetch(`${API_BASE}/commitments/today?developer=${encodeURIComponent(dev)}`)
    .then(r => r.json())
    .then(data => {
      setTodayCommitment(data.record);
      const cf = data.carryForward || [];
      setCarryForward(cf);
      if (!data.hasCommitment && cf.length > 0) setSelectedTicketIds(cf);
      if (data.hasCommitment && data.committed) {
        const init = {};
        (data.committed || []).forEach(t => { init[t] = null; });
        setEveningChecklist(init);
      }
    })
    .catch(() => {});
}, []);

const fetchDevTickets = useCallback((dev) => {
  if (!dev) return;
  fetch(`${API_BASE}/commitments/developer-tickets?developer=${encodeURIComponent(dev)}`)
    .then(r => r.json())
    .then(data => setDevTickets(Array.isArray(data) ? data : []))
    .catch(() => {});
}, []);

const fetchCommitAnalytics = useCallback((dev) => {
  if (!dev) return;
  fetch(`${API_BASE}/commitments/analytics?developer=${encodeURIComponent(dev)}`)
    .then(r => r.json())
    .then(data => setCommitAnalytics(data))
    .catch(() => {});
}, []);

// Team Benchmarking table + selected-developer highlight/rank. Runs on mount
// and whenever the developer or sort changes — the table stays global either way.
useEffect(() => {
  fetchBenchmark(selectedDeveloper, benchmarkSort);
}, [selectedDeveloper, benchmarkSort, fetchBenchmark]);

// Personal Performance — GitHub activity for the selected developer only.
useEffect(() => {
  setDevIntelligence(null);
  if (!selectedDeveloper) return;
  fetch(`${API_BASE}/github/developers/intelligence/${encodeURIComponent(selectedDeveloper)}`)
    .then(r => r.json())
    .then(d => { if (d && !d.error) setDevIntelligence(d); })
    .catch(() => {});
}, [selectedDeveloper]);

// Daily Execution — commitment data for the selected developer only.
useEffect(() => {
  setTodayCommitment(null);
  setDevTickets([]);
  setCommitAnalytics(null);
  setCarryForward([]);
  setEveningChecklist({});
  setSelectedTicketIds([]);
  if (!selectedDeveloper) return;
  fetchTodayCommitment(selectedDeveloper);
  fetchDevTickets(selectedDeveloper);
  fetchCommitAnalytics(selectedDeveloper);
}, [selectedDeveloper, fetchTodayCommitment, fetchDevTickets, fetchCommitAnalytics]);


  useEffect(() => {
    const tickets = todayCommitment?.committedTickets || [];
    if (tickets.length === 0) { setCommitVerification({}); return; }
    tickets.forEach(t => {
      fetch(`${API_BASE}/github/commitment/ticket/${t}`)
        .then(r => r.json())
        .then(d => setCommitVerification(prev => ({ ...prev, [t]: d })))
        .catch(() => {});
    });
  }, [todayCommitment]);

  const [sprint, setSprint] = useState("");
  const [sprints, setSprints] = useState([]);
  const [prioritySort, setPrioritySort] = useState("priority_score");

  useEffect(() => {
    fetch(`${API_BASE}/workload/sprints`)
      .then(r => r.json())
      .then(d => {
        const list = d.sprints || [];
        setSprints(list);
        if (list.length > 0) setSprint(list[0]);
      })
      .catch(() => {});
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    setSyncStatus(null);
    try {
      const res = await fetch(`${API_BASE}/jira/sync`);
      const data = await res.json();
      if (data.success) {
        setSyncStatus("success");
        fetchAllData(dateRange, selectedDeveloper);
      } else {
        setSyncStatus("error");
      }
    } catch {
      setSyncStatus("error");
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncStatus(null), 3000);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    const q = query.toLowerCase();
    const seen = new Set();
    const results = [];

    const addResult = (ticketNo, type, assignedTo) => {
      if (!seen.has(ticketNo) && results.length < 8) {
        seen.add(ticketNo);
        results.push({ ticketNo, type, assignedTo });
      }
    };

    criticalTickets.forEach(t => {
      if (t.ticket?.toLowerCase().includes(q) || t.assignedTo?.toLowerCase().includes(q))
        addResult(t.ticket, "Critical", t.assignedTo);
    });
    ageingTickets.forEach(t => {
      if (t.ticket?.toLowerCase().includes(q) || t.assignedTo?.toLowerCase().includes(q))
        addResult(t.ticket, "Ageing", t.assignedTo);
    });
    quickWinTickets.forEach(t => {
      if (t.ticket?.toLowerCase().includes(q) || t.assignedTo?.toLowerCase().includes(q))
        addResult(t.ticket, "Quick Win", t.assignedTo);
    });
    priorityTickets.forEach(t => {
      if (t.ticketNo?.toLowerCase().includes(q))
        addResult(t.ticketNo, "Priority", null);
    });
    idleTickets.forEach(t => {
      if (t.ticketNo?.toLowerCase().includes(q))
        addResult(t.ticketNo, "Idle", null);
    });

    setSearchResults(results);
  };

  const handleSaveCommitments = async () => {
    if (!selectedDeveloper || selectedTicketIds.length === 0) return;
    setCommitLoading(true);
    try {
      const res = await fetch(`${API_BASE}/commitments/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ developer: selectedDeveloper, tickets: selectedTicketIds }),
      });
      if (res.ok) {
        const data = await res.json();
        setTodayCommitment(data.record);
        const init = {};
        selectedTicketIds.forEach(t => { init[t] = null; });
        setEveningChecklist(init);
        setSelectedTicketIds([]);
        setCommitView("evening");
      }
    } catch (e) { console.error(e); }
    setCommitLoading(false);
  };

  const handleEveningSubmit = async () => {
    if (!selectedDeveloper) return;
    setCommitLoading(true);
    const completedTickets = Object.entries(eveningChecklist)
      .filter(([, v]) => v?.completed === true)
      .map(([k]) => k);
    const reasons = {};
    Object.entries(eveningChecklist).forEach(([t, v]) => {
      if (v?.completed === false && v?.reason) reasons[t] = v.reason;
    });
    try {
      await fetch(`${API_BASE}/commitments/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ developer: selectedDeveloper, completedTickets, reasons }),
      });
      fetchTodayCommitment(selectedDeveloper);
      fetchCommitAnalytics(selectedDeveloper);
      setCommitView("analytics");
    } catch (e) { console.error(e); }
    setCommitLoading(false);
  };

  const sortedPriorityTickets = [...priorityTickets].sort((a, b) => {
    if (prioritySort === "ageing") return (b.ageingDays || 0) - (a.ageingDays || 0);
    if (prioritySort === "priority_weight") return (b.priorityWeight || 0) - (a.priorityWeight || 0);
    if (prioritySort === "sla") return (b.overdueDays || 0) - (a.overdueDays || 0);
    return (b.priorityScore || 0) - (a.priorityScore || 0);
  });

  const filteredDevTickets = devTickets.filter(ticket => {
    if (!commitTicketSearch.trim()) return true;
    const q = commitTicketSearch.toLowerCase();
    return ticket.ticketNo.toLowerCase().includes(q) || (ticket.summary || "").toLowerCase().includes(q);
  });

  const bsd = benchmarkData?.selectedDeveloper || null;
  const bta = benchmarkData?.teamAverage || null;
  const btr = benchmarkData?.teamRanking || null;
  const bts = benchmarkData?.teamSize || 0;
  const bct = benchmarkData?.comparisonTable || [];

  // ── Closure Score (computed from live data) ──────────────────────────────
  // escalatedCount comes from the GLOBAL /tickets/escalation-center feed. It is
  // only mixed into the score in the team ("All Developers") view; a personalized
  // score must not borrow team data, so it is dropped when a developer is selected.
  const escalatedCount = (escalationData?.tickets || []).length;
  const criticalPenalty  = Math.min(Math.round(criticalTickets.length * 2), 20);
  const ageingPenalty    = Math.min(Math.round(ageingCount * 1.5), 20);
  const idlePenalty      = Math.min(Math.round(idle30Count * 1.5), 20);
  const escalationPenalty = selectedDeveloper ? 0 : Math.min(escalatedCount, 15);
  const quickWinReward   = Math.min(quickWinCount, 15);
  const closureScore = Math.max(0, Math.min(100,
    100 - criticalPenalty - ageingPenalty - idlePenalty - escalationPenalty + quickWinReward
  ));
  const scoreColor = closureScore >= 80 ? "#16A34A" : closureScore >= 60 ? "#D97706" : "#DC2626";
  const scoreLabel = closureScore >= 80 ? "Healthy" : closureScore >= 60 ? "Needs Attention" : "At Risk";

  const unassignedCount = (escalationData?.tickets || []).filter(
    (t) => (t.escalationCategories || []).includes("Ownership Risk")
  ).length;

  // ── Derived for new section layouts ──────────────────────────────────────────
  const topPerformer = bct.length > 0 ? [...bct].sort((a,b) => (b.closed||0)-(a.closed||0))[0] : null;
  const healthNarrative = criticalTickets.length > 5
    ? "Critical ticket volume is elevated. Immediate triage recommended to protect sprint delivery."
    : ageingCount > 8
    ? "Team throughput appears steady, but the ageing backlog continues to accumulate. SLA risk is rising."
    : unassignedCount > 3
    ? "Ownership gaps detected across the backlog. Assignment coverage requires immediate attention."
    : "Team execution is on track. Maintain current velocity to close the sprint cleanly.";
  const slaBreachedTickets = ageingTickets.filter(t => t.overdueDays > 0);
  const avgOverdueDays = slaBreachedTickets.length > 0
    ? Math.round(slaBreachedTickets.reduce((s,t) => s + (t.overdueDays||0), 0) / slaBreachedTickets.length)
    : 0;

  return (
    <>
    <div style={{ background: "#F8FAFC", minHeight: "100vh", fontFamily: FONT_FAMILY }}>
      <Sidebar />
      <div style={{ marginLeft: SIDEBAR_WIDTH }}>
      <TopNav
        syncing={syncing}
        onSync={handleSync}
        syncStatus={syncStatus}
        searchQuery={searchQuery}
        onSearchChange={handleSearch}
        searchResults={searchResults}
        sprint={sprint}
        sprints={sprints}
        onSprintChange={setSprint}
      />
      <SectionNav sections={OVERVIEW_SECTIONS} />
      <div id="ov-overview" style={ANCHOR}>
        <TheBrief
          sprint={sprint}
          ticketCount={ticketCount}
          criticalCount={criticalTickets.length}
          ageingCount={ageingCount}
          idleCount={idle30Count}
          loading={personalLoading}
          developers={developerWorkload}
          selectedDeveloper={selectedDeveloper}
          onSelectDeveloper={setSelectedDeveloper}
        />
      </div>
      {/* PERSONAL DEVELOPER SUMMARY — executive snapshot, above the Action Center */}
      <div id="ov-snapshot" style={ANCHOR}>
        <PersonalDeveloperSummary
          loading={personalLoading}
          total={ticketCount}
          byStatus={statusSummary?.byStatus}
          byPriority={statusSummary?.byPriority}
          byType={statusSummary?.byType}
          selectedDeveloper={selectedDeveloper}
        />
      </div>
      {/* SECTIONS 2+3: Action Center + Priority Workspace */}
      <div id="ov-priority" style={ANCHOR}>
        <WorkRequiringAttention
          criticalTickets={criticalTickets}
          ageingCount={ageingCount}
          ageingTickets={ageingTickets}
          quickWinCount={quickWinCount}
          quickWinTickets={quickWinTickets}
          sortedPriorityTickets={sortedPriorityTickets}
          prioritySort={prioritySort}
          onPrioritySortChange={setPrioritySort}
          onTicketClick={openInvestigation}
        />
      </div>


      {/* SECTION 6: Commitment Tracker */}
      <div id="ov-daily" style={ANCHOR}>
      <OverviewSection background="page" divider>
        <div style={{ borderRadius: OVERVIEW.cardRadius, border: `1px solid ${OVERVIEW.cardBorder}`, boxShadow: OVERVIEW.cardShadow, overflow: "hidden" }}>

          {/* Dark header band — same treatment as Risk Radar / Early Warning */}
          <div style={{ background: "#071A2F", padding: "32px 36px" }}>
            <SectionHeader
              light
              eyebrow="Commitment Tracker"
              title="Daily Execution"
              description="Developers commit each morning to specific tickets, then mark them done or not in the evening. GitHub activity badges show whether committed tickets actually have PR work behind them."
              style={{ marginBottom: 0 }}
            />
          </div>

        <div style={{ background: "#FFFFFF", padding: 36 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, color: G.gray500, fontWeight: 500 }}>Developer</span>
              <span style={{ padding: "6px 10px", borderRadius: 4, border: `1px solid ${G.border}`, background: "#fff", fontSize: 12, color: selectedDeveloper ? "#111827" : "#9CA3AF", minWidth: 160, display: "inline-block" }}>
                {selectedDeveloper || "All Developers"}
              </span>
            </div>
            <div style={{ display: "flex", gap: 0, border: "1px solid #BFDBFE", borderRadius: 4, overflow: "hidden" }}>
              {[["morning", "Morning"], ["evening", "Evening Review"], ["analytics", "Analytics"]].map(([view, label]) => (
                <button key={view} onClick={() => setCommitView(view)}
                  style={{ padding: "6px 16px", border: "none", borderRight: view !== "analytics" ? "1px solid #BFDBFE" : "none",
                    background: commitView === view ? "#2563EB" : G.white, color: commitView === view ? G.white : G.gray600,
                    fontWeight: commitView === view ? 600 : 400, fontSize: 12, cursor: "pointer", fontFamily: FONT_FAMILY,
                    transition: "background 0.12s, color 0.12s" }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {!selectedDeveloper ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#6B7280", fontSize: 13 }}>
              Select a developer from the selector above to begin tracking commitments
            </div>
          ) : (
            <>
              {commitView === "morning" && (
                <div>
                  {todayCommitment ? (
                    <div>
                      <div style={{ background: "#EEF4FF", border: "1px solid #BFDBFE", borderRadius: 8, padding: "12px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#16A34A" }}>Commitments saved for today</div>
                          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>
                            {(todayCommitment.committedTickets || []).length} tickets committed · Go to Evening Review to mark completion
                          </div>
                        </div>
                        <button onClick={() => setTodayCommitment(null)}
                          style={{ fontSize: 11, color: "#6B7280", background: "none", border: "1px solid #E5E7EB", borderRadius: 5, padding: "4px 10px", cursor: "pointer" }}>
                          Edit
                        </button>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {(todayCommitment.committedTickets || []).map(t => (
                          <div key={t} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#F8FAFC", borderRadius: 6, border: "1px solid #E5E7EB" }}>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#16A34A", flexShrink: 0, display: "inline-block" }} />
                            <TicketChip ticketKey={t} onClick={openInvestigation} dark={false} style={{ flex: 1 }} />
                            {commitVerification[t] === undefined
                              ? <CommitmentBadge loading={true} />
                              : <CommitmentBadge status={commitVerification[t].commitmentStatus} />
                            }
                            {commitVerification[t]?.linkedPrCount > 0 && (
                              <span style={{ fontSize: 11, color: "#9CA3AF", flexShrink: 0 }}>
                                {commitVerification[t].openPrCount > 0 && `${commitVerification[t].openPrCount} open`}
                                {commitVerification[t].openPrCount > 0 && commitVerification[t].mergedPrCount > 0 && " · "}
                                {commitVerification[t].mergedPrCount > 0 && `${commitVerification[t].mergedPrCount} merged`}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div>
                      {carryForward.length > 0 && (
                        <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "10px 14px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#92400E", marginBottom: 4 }}>Carry Forward From Yesterday</div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {carryForward.map(t => (
                                <span key={t} style={{ fontSize: 11, background: "#FEF3C7", color: "#92400E", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>{t}</span>
                              ))}
                            </div>
                          </div>
                          <button onClick={() => setSelectedTicketIds(prev => [...new Set([...prev, ...carryForward])])}
                            style={{ fontSize: 11, color: "#92400E", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 5, padding: "4px 10px", cursor: "pointer", fontWeight: 600, flexShrink: 0, marginLeft: 12 }}>
                            Recommit All
                          </button>
                        </div>
                      )}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 11, color: "#6B7280" }}>Select tickets to commit to today ({selectedTicketIds.length} selected)</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: 6, padding: "0 10px", height: 32, marginBottom: 8 }}>
                        <span style={{ color: "#9CA3AF", fontSize: 14, flexShrink: 0 }}>⌕</span>
                        <input type="text" value={commitTicketSearch} onChange={e => setCommitTicketSearch(e.target.value)}
                          placeholder="Search by ticket ID or summary..."
                          style={{ flex: 1, border: "none", background: "none", outline: "none", fontSize: 12, color: "#111827" }} />
                        {commitTicketSearch && (
                          <button onClick={() => setCommitTicketSearch("")} style={{ border: "none", background: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
                        )}
                      </div>
                      <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, maxHeight: 300, overflowY: "auto" }}>
                        {devTickets.length === 0 ? (
                          <div style={{ padding: "24px 20px", textAlign: "center", color: "#6B7280", fontSize: 12 }}>No open tickets assigned to this developer</div>
                        ) : filteredDevTickets.length === 0 ? (
                          <div style={{ padding: "20px", textAlign: "center", color: "#9CA3AF", fontSize: 12 }}>No tickets match "{commitTicketSearch}"</div>
                        ) : (
                          filteredDevTickets.map(ticket => {
                            const isSelected = selectedTicketIds.includes(ticket.ticketNo);
                            const isCF = carryForward.includes(ticket.ticketNo);
                            const pMap = { Highest: ["#FEF2F2", "#DC2626"], High: ["#FFF7ED", "#EA580C"], Medium: ["#FFFBEB", "#D97706"], Low: ["#F0FDF4", "#16A34A"], Lowest: ["#F9FAFB", "#9CA3AF"] };
                            const [pbg, ptxt] = pMap[ticket.priority] || ["#F9FAFB", "#9CA3AF"];
                            return (
                              <div key={ticket.ticketNo} onClick={() => setSelectedTicketIds(prev => isSelected ? prev.filter(t => t !== ticket.ticketNo) : [...prev, ticket.ticketNo])}
                                style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: "1px solid #F1F5F9", cursor: "pointer", background: isSelected ? "#F0FDF4" : "#fff" }}>
                                <input type="checkbox" checked={isSelected} onChange={() => {}} style={{ marginTop: 1, cursor: "pointer", flexShrink: 0, accentColor: "#16A34A" }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                                    <TicketChip ticketKey={ticket.ticketNo} onClick={(key, e) => { e.stopPropagation(); openInvestigation(key); }} dark={false} />
                                    {isCF && <span style={{ fontSize: 10, background: "#FEF3C7", color: "#92400E", padding: "1px 6px", borderRadius: 8, fontWeight: 600 }}>CF</span>}
                                    {ticket.priority && <span style={{ fontSize: 10, background: pbg, color: ptxt, padding: "1px 6px", borderRadius: 8, fontWeight: 600 }}>{ticket.priority}</span>}
                                    {ticket.status && <span style={{ fontSize: 10, background: "#F1F5F9", color: "#6B7280", padding: "1px 6px", borderRadius: 8 }}>{ticket.status}</span>}
                                  </div>
                                  <div style={{ fontSize: 11, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ticket.summary || "No summary"}</div>
                                </div>
                                {ticket.ageingDays != null && <span style={{ fontSize: 11, color: "#9CA3AF", flexShrink: 0 }}>{ticket.ageingDays}d</span>}
                              </div>
                            );
                          })
                        )}
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
                        <button onClick={handleSaveCommitments} disabled={commitLoading || selectedTicketIds.length === 0}
                          style={{ background: selectedTicketIds.length > 0 ? G.brand : G.gray200, color: selectedTicketIds.length > 0 ? G.white : G.gray400, border: "none", padding: "10px 22px", borderRadius: 4, fontWeight: 600, cursor: selectedTicketIds.length > 0 ? "pointer" : "not-allowed", fontSize: 13, fontFamily: FONT_FAMILY }}>
                          {commitLoading ? "Saving..." : "Save Commitments"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {commitView === "evening" && (
                <div>
                  {!todayCommitment ? (
                    <div style={{ textAlign: "center", padding: "30px 20px", color: "#6B7280", fontSize: 13 }}>
                      <div style={{ marginBottom: 10 }}>No morning commitments found for today.</div>
                      <button onClick={() => setCommitView("morning")} style={{ fontSize: 12, color: "#16A34A", background: "none", border: "1px solid #16A34A", borderRadius: 4, padding: "5px 14px", cursor: "pointer" }}>
                        Go to Morning
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 14 }}>Mark each ticket as completed or not:</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
                        {(todayCommitment.committedTickets || []).map(ticketNo => {
                          const state = eveningChecklist[ticketNo];
                          const isDone = state?.completed === true;
                          const isNotDone = state?.completed === false;
                          return (
                            <div key={ticketNo} style={{ background: isDone ? "#EEF4FF" : isNotDone ? "#FEF2F2" : "#F9FAFB", border: `1px solid ${isDone ? "#BFDBFE" : isNotDone ? "#FECACA" : "#E5E7EB"}`, borderRadius: 8, padding: "10px 14px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <TicketChip ticketKey={ticketNo} onClick={openInvestigation} dark={false} style={{ minWidth: 80 }} />
                                {commitVerification[ticketNo] === undefined
                                  ? <CommitmentBadge loading={true} />
                                  : <CommitmentBadge status={commitVerification[ticketNo].commitmentStatus} />
                                }
                                <div style={{ display: "flex", gap: 6 }}>
                                  <button onClick={() => setEveningChecklist(prev => ({ ...prev, [ticketNo]: { completed: true, reason: "" } }))}
                                    style={{ padding: "4px 12px", borderRadius: 4, border: `1.5px solid ${isDone ? "#16A34A" : "#E5E7EB"}`, background: isDone ? "#16A34A" : "#fff", color: isDone ? "#fff" : "#6B7280", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                                    Done
                                  </button>
                                  <button onClick={() => setEveningChecklist(prev => ({ ...prev, [ticketNo]: { completed: false, reason: (prev[ticketNo] || {}).reason || "" } }))}
                                    style={{ padding: "4px 12px", borderRadius: 4, border: `1.5px solid ${isNotDone ? "#DC2626" : "#E5E7EB"}`, background: isNotDone ? "#FEF2F2" : "#fff", color: isNotDone ? "#DC2626" : "#6B7280", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                                    Not Done
                                  </button>
                                </div>
                              </div>
                              {isNotDone && (
                                <div style={{ marginTop: 8 }}>
                                  <select value={(state || {}).reason || ""}
                                    onChange={e => setEveningChecklist(prev => ({ ...prev, [ticketNo]: { ...prev[ticketNo], reason: e.target.value } }))}
                                    style={{ padding: "5px 10px", borderRadius: 4, border: "1px solid #E5E7EB", background: "#fff", fontSize: 11, color: "#111827", width: "100%", maxWidth: 280 }}>
                                    <option value="">Select reason (optional)</option>
                                    {["Blocked Dependency", "Waiting For Review", "Waiting For QA", "Scope Underestimated", "Production Support", "Reprioritized", "Other"].map(r => (
                                      <option key={r} value={r}>{r}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button onClick={handleEveningSubmit} disabled={commitLoading}
                          style={{ background: G.brand, color: G.white, border: "none", padding: "10px 22px", borderRadius: 4, fontWeight: 600, cursor: "pointer", fontSize: 13, fontFamily: FONT_FAMILY }}>
                          {commitLoading ? "Submitting..." : "Submit Evening Review"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {commitView === "analytics" && (
                <div>
                  {!commitAnalytics || commitAnalytics.totalDays === 0 ? (
                    <div style={{ textAlign: "center", padding: "40px 20px", color: "#6B7280", fontSize: 13 }}>
                      No commitment data yet. Start by saving your morning commitments.
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
                        {[
                          { label: "Commitment Reliability", value: `${commitAnalytics.commitmentReliability}%`, color: "#16A34A", sub: "Days fully completed" },
                          { label: "Avg Daily Commitments", value: commitAnalytics.averageDailyCommitments, color: G.gray800, sub: "Tickets committed / day" },
                          { label: "Avg Daily Completion", value: commitAnalytics.averageDailyCompletion, color: G.gray800, sub: "Tickets done / day" },
                          { label: "Carry Forward Rate", value: `${commitAnalytics.carryForwardRate}%`, color: "#D97706", sub: "Days with carry-over" },
                        ].map((m, i) => (
                          <div key={i} style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 4, padding: 16 }}>
                            <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 8 }}>{m.label}</div>
                            <div style={{ fontSize: 26, fontWeight: 700, color: m.color, marginBottom: 4 }}>{m.value}</div>
                            <div style={{ fontSize: 11, color: "#9CA3AF" }}>{m.sub}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
                        <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 4, padding: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 4 }}>Daily Commitments vs Completions</div>
                          <Description style={{ marginBottom: 8 }}>How many tickets were committed to each day versus actually finished.</Description>
                          <div style={{ display: "flex", gap: 14, marginBottom: 10 }}>
                            <span style={{ fontSize: 11, color: "#6B7280", display: "flex", alignItems: "center", gap: 4 }}>
                              <span style={{ width: 12, height: 3, background: "#3B82F6", borderRadius: 2, display: "inline-block" }} /> Committed
                            </span>
                            <span style={{ fontSize: 11, color: "#6B7280", display: "flex", alignItems: "center", gap: 4 }}>
                              <span style={{ width: 12, height: 3, background: "#16A34A", borderRadius: 2, display: "inline-block" }} /> Completed
                            </span>
                          </div>
                          <CommitBarChart data={commitAnalytics.dailyTrend} />
                        </div>
                        <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 4, padding: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 4 }}>Carry Forward Trend</div>
                          <Description style={{ marginBottom: 8 }}>How many uncompleted tickets got carried over to the next day, over time.</Description>
                          <div style={{ display: "flex", gap: 14, marginBottom: 10 }}>
                            <span style={{ fontSize: 11, color: "#6B7280", display: "flex", alignItems: "center", gap: 4 }}>
                              <span style={{ width: 12, height: 3, background: "#D97706", borderRadius: 2, display: "inline-block" }} /> Tickets carried forward
                            </span>
                          </div>
                          <CommitLineChart data={commitAnalytics.dailyTrend} />
                        </div>
                      </div>
                      {Object.keys(commitAnalytics.blockerDistribution || {}).length > 0 && (
                        <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 4, padding: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#111827", marginBottom: 4 }}>Blocker Distribution</div>
                          <Description style={{ marginBottom: 10 }}>Reasons given for not completing a committed ticket, ranked by how often each was selected.</Description>
                          {(() => {
                            const entries = Object.entries(commitAnalytics.blockerDistribution).sort((a, b) => b[1] - a[1]);
                            const maxB = Math.max(...entries.map(([, v]) => v), 1);
                            return entries.map(([reason, count]) => (
                              <div key={reason} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                                <div style={{ width: 160, fontSize: 11, color: "#6B7280", textAlign: "right", flexShrink: 0 }}>{reason}</div>
                                <div style={{ flex: 1, height: 18, background: "#F1F5F9", borderRadius: 2, overflow: "hidden" }}>
                                  <div style={{ width: `${Math.round((count / maxB) * 100)}%`, height: "100%", background: "#DC2626", borderRadius: 2 }} />
                                </div>
                                <div style={{ width: 24, fontSize: 11, fontWeight: 700, color: "#111827", textAlign: "right", flexShrink: 0 }}>{count}</div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        </div>
      </OverviewSection>
      </div>

      {/* SECTION 5: Risk Radar */}
      <div id="ov-threats" style={ANCHOR}>
      <OverviewSection background="page" divider>
        <div style={{ borderRadius: OVERVIEW.cardRadius, border: `1px solid ${OVERVIEW.cardBorder}`, boxShadow: OVERVIEW.cardShadow, overflow: "hidden" }}>
          <div style={{ background: "#071A2F", padding: "32px 36px" }}>
            <SectionHeader
              light
              eyebrow="Risk Radar"
              title="Threats to Delivery"
              description="Workflow friction and PR bottlenecks — click any ticket to investigate."
              style={{ marginBottom: 0 }}
            />
          </div>

          <div style={{ background: "#071A2F", padding: "0 36px 36px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>

            {/* Workflow Bounce */}
            <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, padding: "28px 28px 24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)" }}>Workflow Bounce</span>
                    {selectedDeveloper && (
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", padding: "2px 7px", borderRadius: 3, background: "rgba(147,197,253,0.16)", color: "#93C5FD", border: "1px solid rgba(147,197,253,0.3)" }}>
                        {selectedDeveloper}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#FFFFFF" }}>Status Regression Detection</div>
                  <Description light style={{ marginTop: 6, maxWidth: 340 }}>
                    Tickets that moved backward through the workflow, e.g. Testing → In Progress — a sign of rework or instability.
                  </Description>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
                <div
                  onClick={() => setExpandBounce(v => !v)}
                  style={{ background: expandBounce ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.06)", borderRadius: 4, padding: "16px 20px", cursor: "pointer", transition: "background 0.12s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = expandBounce ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.06)"; }}
                >
                  <div style={{ fontSize: 32, fontWeight: 700, color: bounceData && bounceData.ticketsAffected > 0 ? "#FCA5A5" : "rgba(255,255,255,0.75)", letterSpacing: "-0.02em" }}>
                    {bounceData ? bounceData.ticketsAffected : "—"}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4 }}>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>Affected Tickets</div>
                    <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)" }}>{expandBounce ? "▲" : "▼"}</span>
                  </div>
                </div>
                <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 4, padding: "16px 20px" }}>
                  <div style={{ fontSize: 32, fontWeight: 700, color: bounceData && bounceData.bounceRate > 20 ? "#FCA5A5" : "rgba(255,255,255,0.75)", letterSpacing: "-0.02em" }}>
                    {bounceData ? bounceData.bounceRate + "%" : "—"}
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>Bounce Rate</div>
                </div>
              </div>
              {!bounceData && (
                <div style={{ padding: "10px 0", borderTop: "1px solid rgba(255,255,255,0.08)", fontSize: 12, color: "rgba(255,255,255,0.3)" }}>Loading…</div>
              )}
              {expandBounce && bounceData && (bounceData.topBouncingTickets ?? []).length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", lineHeight: 1.65, marginBottom: 14, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 12 }}>
                    A ticket is counted as affected when its status was moved backward in the workflow at least once — e.g. Testing → In Progress — indicating rework or instability. Tickets with higher counts have regressed more frequently.
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: 8 }}>
                    Top Affected Tickets
                  </div>
                  <div className="themed-scroll-dark" style={{ maxHeight: "min(50vh, 400px)", overflowY: "auto" }}>
                    {bounceData.topBouncingTickets.map(t => (
                      <div key={t.ticket}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 4px", borderTop: "1px solid rgba(255,255,255,0.07)", borderRadius: 4, margin: "0 -4px", transition: "background 0.12s" }}>
                        <TicketChip ticketKey={t.ticket} onClick={openInvestigation} />
                        <span style={{ fontSize: 12, color: t.count >= 3 ? "#FCA5A5" : "rgba(255,255,255,0.45)" }}>{t.count}× bounced</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* PR Review Queue */}
            <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, padding: "28px 28px 24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)" }}>PR Review Queue</span>
                    {selectedDeveloper && (
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 7px", borderRadius: 3, background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.16)" }}>
                        Team-wide
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#FFFFFF" }}>Pull Requests Awaiting Review</div>
                  <Description light style={{ marginTop: 6, maxWidth: 340 }}>
                    Open, unmerged pull requests linked to tickets. PRs open more than 14 days are flagged as waiting.
                  </Description>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
                <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 4, padding: "16px 20px" }}>
                  <div style={{ fontSize: 32, fontWeight: 700, color: "rgba(255,255,255,0.85)", letterSpacing: "-0.02em" }}>{githubRisk?.prs?.awaitingReview ?? earlyWarning?.reviewWaitTime?.totalAwaiting ?? "—"}</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>Awaiting Review</div>
                </div>
                <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 4, padding: "16px 20px" }}>
                  <div style={{ fontSize: 32, fontWeight: 700, color: (githubRisk?.prs?.staleCount || earlyWarning?.reviewWaitTime?.prsOver14d || 0) > 2 ? "#FCA5A5" : "rgba(255,255,255,0.75)", letterSpacing: "-0.02em" }}>{githubRisk?.prs?.staleCount ?? earlyWarning?.reviewWaitTime?.prsOver14d ?? "—"}</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>Waiting {">"}14d</div>
                </div>
              </div>
              {(() => {
                const prs = earlyWarning?.reviewWaitTime?.prs ?? [];
                if (prs.length === 0 && !githubRisk) return <div style={{ fontSize: 12, color: "rgba(255,255,255,0.25)", fontStyle: "italic" }}>GitHub analytics disabled</div>;
                if (prs.length === 0) return null;
                const visible = expandPRQueue ? prs : prs.slice(0, 5);
                return (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: 8 }}>Open PRs</div>
                    <div className={expandPRQueue ? "themed-scroll-dark" : undefined} style={{ maxHeight: expandPRQueue ? "min(50vh, 400px)" : "none", overflowY: expandPRQueue ? "auto" : "visible" }}>
                      {visible.map(pr => (
                        <a key={pr.prNumber} href={pr.url || `https://github.com/${pr.repository}/pull/${pr.prNumber}`} target="_blank" rel="noopener noreferrer"
                          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 4px", borderTop: "1px solid rgba(255,255,255,0.07)", borderRadius: 4, margin: "0 -4px", textDecoration: "none", transition: "background 0.12s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <div style={{ minWidth: 0 }}>
                            <span style={{ fontFamily: TYPE.mono.fontFamily, fontSize: 12, color: "#93C5FD", fontWeight: 600 }}>#{pr.prNumber}</span>
                            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginLeft: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200, display: "inline-block", verticalAlign: "bottom" }}>{pr.prTitle}</span>
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: pr.ageDays >= 14 ? "#FCA5A5" : "rgba(255,255,255,0.4)", flexShrink: 0, marginLeft: 8 }}>{pr.ageDays}d</span>
                        </a>
                      ))}
                    </div>
                    {prs.length > 5 && (
                      <button onClick={() => setExpandPRQueue(v => !v)}
                        style={{ marginTop: 12, padding: "6px 14px", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 3, color: "rgba(255,255,255,0.45)", fontSize: 11, cursor: "pointer", fontFamily: FONT_FAMILY, transition: "border-color 0.12s, color 0.12s" }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)"; e.currentTarget.style.color = "#FFFFFF"; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; e.currentTarget.style.color = "rgba(255,255,255,0.45)"; }}>
                        {expandPRQueue ? "▲ Show less" : `▼ Show ${prs.length - 5} more`}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </OverviewSection>
      </div>

      {/* SECTION 4: Developer Summary | Team Benchmarking */}
      <div id="ov-team" style={ANCHOR}>
      <OverviewSection background="page" divider>
      <div style={{
        display: "flex",
        fontFamily: FONT_FAMILY,
        alignItems: "stretch",
        borderRadius: OVERVIEW.cardRadius,
        border: `1px solid ${OVERVIEW.cardBorder}`,
        boxShadow: OVERVIEW.cardShadow,
        overflow: "hidden",
      }}>

        {/* LEFT: Developer Summary */}
        <div style={{ flex: "0 0 35%", background: "#071A2F", padding: 40, display: "flex", flexDirection: "column" }}>

          <SectionHeader
            light
            eyebrow="Developer Summary"
            title="Personal Performance"
            description="A selected developer's own ticket load and delivery stats, ranked against the rest of the team."
            descriptionMaxWidth={320}
          />

          {/* Reflects the shared Developer selector in the hero */}
          <div style={{ width: "100%", padding: "9px 14px", borderRadius: 4, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.07)", color: selectedDeveloper ? "#FFFFFF" : "rgba(255,255,255,0.45)", fontSize: 13, fontFamily: FONT_FAMILY, marginBottom: 32 }}>
            {selectedDeveloper || "Choose a developer from the selector above"}
          </div>

          {/* Personal KPIs */}
          {bsd ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px", marginBottom: 32 }}>
                {[
                  { label: "Closed", value: bsd.closed, rank: btr?.closed, good: true },
                  { label: "Open", value: bsd.open, rank: btr?.open, good: false },
                  { label: "Avg Age", value: `${bsd.avgAge}d`, rank: btr?.avgAge, good: false },
                  { label: "SLA %", value: `${bsd.slaCompliance}%`, rank: btr?.slaCompliance, good: true },
                  { label: "High Priority", value: bsd.highPriority, rank: btr?.highPriority, good: false },
                  { label: "Quick Wins", value: bsd.quickWinCount, rank: btr?.quickWinCount, good: true },
                ].map(m => (
                  <div key={m.label}>
                    <div style={{ fontSize: 30, fontWeight: 700, color: "#FFFFFF", lineHeight: 1, letterSpacing: "-0.02em", marginBottom: 4 }}>
                      {m.value}
                    </div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.38)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{m.label}</div>
                    {m.rank != null && bts > 0 && (
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", marginTop: 3 }}>
                        Rank {m.rank} of {bts} · {m.good ? "higher is better" : "lower is better"}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ height: 1, background: "rgba(255,255,255,0.1)", marginBottom: 28 }} />
            </>
          ) : (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 28 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 32, color: "rgba(255,255,255,0.1)", marginBottom: 12 }}>↑</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.3)", lineHeight: 1.6 }}>
                  Select a developer above<br />to view personal KPIs
                </div>
              </div>
            </div>
          )}

          {/* Top Performer */}
          {topPerformer && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 10 }}>
                Top Performer
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#FFFFFF", marginBottom: 4 }}>{topPerformer.developer}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>
                {topPerformer.closed} closed · SLA {topPerformer.slaCompliance}%
              </div>
            </div>
          )}

          <div style={{ height: 1, background: "rgba(255,255,255,0.1)", marginBottom: 24 }} />

          {/* GitHub Intelligence */}
          {devIntelligence && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 4 }}>GitHub · Activity</div>
              <Description light style={{ marginBottom: 12, fontSize: 11 }}>
                PR Coverage is the % of this developer's tickets with a linked pull request. Risk Tickets have a flagged PR issue — see below.
              </Description>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px", marginBottom: 12 }}>
                {[
                  { label: "Tickets",      value: devIntelligence.assignedTickets },
                  { label: "With PR",      value: devIntelligence.ticketsWithPRActivity },
                  { label: "Open PRs",     value: devIntelligence.openPRs },
                  { label: "Merged PRs",   value: devIntelligence.mergedPRs },
                  { label: "PR Coverage",  value: `${devIntelligence.prCoveragePct}%` },
                  { label: "Risk Tickets", value: devIntelligence.riskTickets, alert: devIntelligence.riskTickets > 0 },
                ].map(m => (
                  <div key={m.label}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: m.alert ? "#FCA5A5" : "#FFFFFF", lineHeight: 1, letterSpacing: "-0.02em", marginBottom: 2 }}>{m.value}</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.38)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{m.label}</div>
                  </div>
                ))}
              </div>
              {devIntelligence.attentionFlags.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
                  {devIntelligence.attentionFlags.map((f, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#FCD34D", display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 9 }}>▲</span>{f}
                    </div>
                  ))}
                </div>
              )}
              <ScoreBreakdown
                dark
                title="What makes a ticket a GitHub risk"
                factors={[
                  { label: "No PR while assigned and not done", points: "flag" },
                  { label: "Open PR stale (past the stale threshold)", points: "flag" },
                  { label: "Open PR waiting on review", points: "flag" },
                  { label: "Merged PR but ticket not marked done", points: "flag" },
                  { label: "PRs open across multiple repos, partially merged", points: "flag" },
                ]}
                footnote="Risk Tickets = tickets with 1 or more of these flags."
              />
            </div>
          )}
          {devIntelligence && <div style={{ height: 1, background: "rgba(255,255,255,0.1)", marginBottom: 24 }} />}

          {/* Health narrative */}
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.75 }}>
            {healthNarrative}
          </div>
        </div>

        {/* RIGHT: Team Benchmarking */}
        <div style={{ flex: 1, background: "#FFFFFF", padding: 40, overflow: "auto" }}>

          <SectionHeader
            eyebrow="Team Benchmarking"
            title="How does this developer compare?"
            description="Every developer ranked side by side on open load, throughput, ticket age, and risk exposure."
            descriptionMaxWidth={460}
            style={{ paddingBottom: 20, borderBottom: `1px solid ${G.border}` }}
            actions={
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10, flexShrink: 0 }}>
                <select value={benchmarkSort} onChange={e => setBenchmarkSort(e.target.value)}
                  style={{ padding: "7px 12px", borderRadius: 4, border: `1px solid ${G.border}`, background: G.white, fontSize: 12, color: G.gray700, fontFamily: FONT_FAMILY, cursor: "pointer", outline: "none", flexShrink: 0 }}>
                  <option value="closed">Most Closed</option>
                  <option value="sla">Best SLA</option>
                  <option value="avgAge">Lowest Avg Age</option>
                  <option value="workload">Highest Workload</option>
                </select>
                <ScoreBreakdown
                  title="How rankings are calculated"
                  intro="Each column ranks every developer independently — there's no single combined score."
                  factors={[
                    { label: "Open, Avg Age, High Priority, Critical", points: "lower is better" },
                    { label: "Closed, Quick Wins, SLA %", points: "higher is better" },
                  ]}
                  footnote={'"Critical" recomputes the same Risk Score used in the Critical Issues panel and counts tickets scoring 60+.'}
                />
              </div>
            }
          />

          {/* Table */}
          {bct.length > 0 ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "2.5fr 1fr 1fr 1fr 1fr 1fr", padding: "12px 0 12px 16px", borderBottom: `1px solid ${G.border}` }}>
                {["Developer", "Open", "Closed", "Avg Age", "Critical", "Risk"].map(h => (
                  <div key={h} style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: G.gray400 }}>{h}</div>
                ))}
              </div>
              {bct.slice(0, 15).map((row, idx) => {
                const isSelected = row.developer === selectedDeveloper;
                const intel = allDevIntelligence[row.developer];
                return (
                  <div key={idx} style={{ display: "grid", gridTemplateColumns: "2.5fr 1fr 1fr 1fr 1fr 1fr", padding: "16px 0 16px 16px", borderBottom: `1px solid ${G.border}`, borderLeft: isSelected ? `3px solid #071A2F` : "3px solid transparent", background: isSelected ? "#F0F4F8" : G.white, transition: "background 0.1s" }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = G.gray50; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = G.white; }}>
                    <div style={{ fontWeight: isSelected ? 700 : 400, color: G.gray900, fontSize: 13, display: "flex", alignItems: "center", gap: 8, paddingRight: 12 }}>
                      {isSelected && <span style={{ color: "#071A2F", fontSize: 9 }}>▶</span>}
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.developer}</span>
                      {intel?.attentionFlags?.length > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 700, background: "rgba(220,38,38,0.08)", color: G.red600, padding: "2px 6px", borderRadius: 2, letterSpacing: "0.04em", flexShrink: 0 }}>ATTN</span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: G.gray700 }}>{row.open}</div>
                    <div style={{ fontSize: 13, color: G.gray900, fontWeight: 600 }}>{row.closed}</div>
                    <div style={{ fontSize: 13, color: row.avgAge > (bta?.avgAge||0) ? G.red600 : G.gray700, fontWeight: row.avgAge > (bta?.avgAge||0) ? 600 : 400 }}>{row.avgAge}d</div>
                    <div style={{ fontSize: 13, color: row.criticalCount > 0 ? G.red600 : G.gray400, fontWeight: row.criticalCount > 0 ? 700 : 400 }}>{row.criticalCount}</div>
                    <div style={{ fontSize: 13, fontWeight: intel?.riskTickets > 0 ? 700 : 400, color: intel?.riskTickets > 0 ? G.red600 : G.gray400 }}>
                      {intel != null ? intel.riskTickets : "—"}
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div style={{ padding: "64px 0", textAlign: "center", color: G.gray400, fontSize: 13 }}>
              Loading team data...
            </div>
          )}
        </div>
      </div>
      </OverviewSection>

      {/* GITHUB DEVELOPER ANALYTICS */}
      {githubDevelopers.length > 0 && (
        <OverviewSection background="page" divider>
          <SectionHeader
            eyebrow="GitHub · Developer Activity"
            title="GitHub Contribution Metrics"
            description="Raw commit and pull request activity per GitHub user over the last 30 days, independent of Jira ticket assignment."
            descriptionMaxWidth={560}
          />
          <div style={{ background: G.white, border: `1px solid ${OVERVIEW.panelBorder}`, borderRadius: OVERVIEW.panelRadius, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr", padding: "10px 16px", borderBottom: `1px solid ${G.border}`, background: G.gray50 }}>
              {["GitHub User", "Commits (30d)", "Commits (7d)", "PRs Opened", "Reviews Given", "Last Active"].map(h => (
                <div key={h} style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: G.gray400 }}>{h}</div>
              ))}
            </div>
            {githubDevelopers.map((d, i) => (
              <div key={d.githubUser} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr", padding: "12px 16px", borderBottom: i < githubDevelopers.length - 1 ? `1px solid ${G.border}` : "none", background: G.white }}
                onMouseEnter={e => e.currentTarget.style.background = G.gray50}
                onMouseLeave={e => e.currentTarget.style.background = G.white}>
                <div style={{ fontWeight: 600, color: G.gray900, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#EFF6FF", border: "1px solid #BFDBFE", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#2563EB", flexShrink: 0 }}>
                    {d.githubUser.slice(0, 2).toUpperCase()}
                  </div>
                  {d.githubUser}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: d.commitsLast30Days > 0 ? G.green600 : G.gray400 }}>{d.commitsLast30Days}</div>
                <div style={{ fontSize: 13, color: G.gray700 }}>{d.commitsLast7Days}</div>
                <div style={{ fontSize: 13, color: G.gray700 }}>{d.prsOpened}</div>
                <div style={{ fontSize: 13, color: G.gray700 }}>{d.reviewsGiven}</div>
                <div style={{ fontSize: 12, color: G.gray500 }}>{d.lastActivity ? new Date(d.lastActivity).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}</div>
              </div>
            ))}
          </div>
        </OverviewSection>
      )}
      </div>

      {/* PHASE 7B: EARLY WARNING SYSTEM */}
      {earlyWarning && (() => {
        const PC = (p) => (p === "Critical" || p === "Highest") ? "#FCA5A5" : "rgba(255,255,255,0.55)";
        const RISK_LEVEL_COLORS = { LOW: "rgba(255,255,255,0.6)", MODERATE: "rgba(255,255,255,0.75)", HIGH: "#FCA5A5", CRITICAL: "#FCA5A5" };
        const RISK_LEVEL_BG = { LOW: "rgba(255,255,255,0.06)", MODERATE: "rgba(255,255,255,0.06)", HIGH: "rgba(220,38,38,0.12)", CRITICAL: "rgba(220,38,38,0.15)" };
        const toggleSignal = (id) => setExpandedSignal(prev => prev === id ? null : id);

        const TicketTable = ({ headers, rows }) => (
          <div className="themed-scroll-dark" style={{ maxHeight: "min(60vh, 480px)", overflowY: "auto", overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 4 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
                  {headers.map(h => <th key={h} style={{ position: "sticky", top: 0, background: "#071A2F", textAlign: "left", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.3)", letterSpacing: "0.08em", textTransform: "uppercase", padding: "0 12px 10px 0" }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
        );

        const SignalRow = ({ id, dot, label, def, summary, count, countColor, children }) => {
          const open = expandedSignal === id;
          return (
            <div style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <div onClick={() => toggleSignal(id)}
                onMouseEnter={e => { if (!open) e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "22px 4px", cursor: "pointer", userSelect: "none", borderRadius: 6, margin: "0 -4px", transition: "background 0.15s" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: dot, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 3 }}>{label}</div>
                    {def && <Description light style={{ marginBottom: 4, maxWidth: 460 }}>{def}</Description>}
                    <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>{summary}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 20, flexShrink: 0 }}>
                  <span style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-0.02em", color: countColor }}>{count}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "rgba(255,255,255,0.3)", width: 72, textAlign: "right" }}>{open ? "▲ CLOSE" : "▼ EXPAND"}</span>
                </div>
              </div>
              <div style={{ maxHeight: open ? 1600 : 0, overflow: "hidden", transition: "max-height 0.35s ease" }}>
                <div style={{ paddingTop: 4, paddingBottom: 24 }}>{children}</div>
              </div>
            </div>
          );
        };

        const tdBase = { padding: "10px 12px 10px 0", verticalAlign: "middle" };
        const monoStyle = { ...tdBase, fontFamily: TYPE.mono.fontFamily, fontSize: 13, fontWeight: 600, color: "#93C5FD" };

        return (
          <div id="ov-early-warning" style={ANCHOR}>
          <OverviewSection background="page" divider>
            <div style={{ borderRadius: OVERVIEW.cardRadius, border: `1px solid ${OVERVIEW.cardBorder}`, boxShadow: OVERVIEW.cardShadow, overflow: "hidden" }}>

              {/* Header */}
              <div style={{ background: "#071A2F", padding: "32px 36px" }}>
                <SectionHeader
                  light
                  eyebrow="Early Warning System"
                  title="What Is Likely to Go Wrong Before It Happens?"
                  description="Four forward-looking checks, run on live ticket and PR data, that surface problems before they become blockers. Expand any signal to see affected tickets."
                  style={{ marginBottom: 0 }}
                />
              </div>

              {/* Accordion body */}
              <div style={{ background: "#071A2F", padding: "0 36px 12px" }}>

                {/* ── SLA Breach ─────────────────────────────────────────────── */}
                <SignalRow
                  id="sla"
                  dot="#FCA5A5"
                  label="SLA Breach Forecast"
                  def="Tickets that have already missed, or are within 7 days of missing, their priority-based resolution target."
                  summary={`${earlyWarning.imminentSlaBreach.within7d.toLocaleString()} tickets overdue or within 7 days of SLA limit`}
                  count={earlyWarning.imminentSlaBreach.within7d.toLocaleString()}
                  countColor="#FCA5A5"
                >
                  <TicketTable
                    headers={["Ticket", "Priority", "Days Left", "Assignee", "Status"]}
                    rows={earlyWarning.imminentSlaBreach.tickets.slice(0, 30).map(t => (
                      <tr key={t.ticketKey} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                        <td style={tdBase}><TicketChip ticketKey={t.ticketKey} onClick={openInvestigation} /></td>
                        <td style={{ ...tdBase, fontSize: 12, fontWeight: 600, color: PC(t.priority) }}>{t.priority ?? "—"}</td>
                        <td style={{ ...tdBase, fontSize: 13, fontWeight: 700, color: t.daysUntilBreach <= 0 ? "#FCA5A5" : "rgba(255,255,255,0.75)" }}>
                          {t.daysUntilBreach <= 0 ? `${Math.abs(t.daysUntilBreach)}d overdue` : `${t.daysUntilBreach}d left`}
                        </td>
                        <td style={{ ...tdBase, fontSize: 12, color: "rgba(255,255,255,0.6)", maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.assignee ?? "—"}</td>
                        <td style={{ ...tdBase, fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{t.status ?? "—"}</td>
                      </tr>
                    ))}
                  />
                  {earlyWarning.imminentSlaBreach.tickets.length > 30 && (
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginTop: 12, fontStyle: "italic" }}>
                      + {(earlyWarning.imminentSlaBreach.tickets.length - 30).toLocaleString()} more tickets breaching SLA
                    </div>
                  )}
                </SignalRow>

                {/* ── Testing Bottleneck ─────────────────────────────────────── */}
                <SignalRow
                  id="testing"
                  dot="rgba(255,255,255,0.35)"
                  label="Testing Bottleneck"
                  def="Tickets currently sitting in the Testing status, and how many have been stuck there for 30 or more days."
                  summary={`${earlyWarning.testingBottleneck.stuckInTestingCount} tickets in Testing · ${earlyWarning.testingBottleneck.stuckOver30d} stuck > 30 days`}
                  count={earlyWarning.testingBottleneck.stuckInTestingCount}
                  countColor="rgba(255,255,255,0.75)"
                >
                  <TicketTable
                    headers={["Ticket", "Assignee", "Days in Testing", "Priority", "Bounces"]}
                    rows={earlyWarning.testingBottleneck.tickets.slice(0, 30).map(t => (
                      <tr key={t.ticketKey} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                        <td style={tdBase}><TicketChip ticketKey={t.ticketKey} onClick={openInvestigation} /></td>
                        <td style={{ ...tdBase, fontSize: 12, color: "rgba(255,255,255,0.6)", maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.assignee ?? "—"}</td>
                        <td style={{ ...tdBase, fontSize: 13, fontWeight: 700, color: t.daysInTesting >= 30 ? "#FCA5A5" : "rgba(255,255,255,0.75)" }}>
                          {t.daysInTesting}d
                        </td>
                        <td style={{ ...tdBase, fontSize: 12, fontWeight: 600, color: PC(t.priority) }}>{t.priority ?? "—"}</td>
                        <td style={{ ...tdBase, fontSize: 13, fontWeight: 700, color: t.bounceCount > 0 ? "#FCA5A5" : "rgba(255,255,255,0.3)" }}>
                          {t.bounceCount > 0 ? `${t.bounceCount}×` : "—"}
                        </td>
                      </tr>
                    ))}
                  />
                  {earlyWarning.testingBottleneck.tickets.length > 30 && (
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginTop: 12, fontStyle: "italic" }}>
                      + {earlyWarning.testingBottleneck.tickets.length - 30} more tickets in Testing
                    </div>
                  )}
                </SignalRow>

                {/* ── Sprint Miss Risk ──────────────────────────────────────── */}
                <SignalRow
                  id="sprint"
                  dot={RISK_LEVEL_COLORS[earlyWarning.sprintMissRisk.riskLevel]}
                  label="Sprint Miss Risk"
                  def="Estimated days to clear the active backlog at the team's recent closing pace, rated LOW to CRITICAL."
                  summary={`${earlyWarning.sprintMissRisk.estimatedDaysToClose >= 9999 ? "∞" : earlyWarning.sprintMissRisk.estimatedDaysToClose.toLocaleString()} est. days at ${earlyWarning.sprintMissRisk.avgDailyVelocity} tickets/day`}
                  count={<span style={{ fontSize: 18, padding: "4px 12px", borderRadius: 4, background: RISK_LEVEL_BG[earlyWarning.sprintMissRisk.riskLevel], color: RISK_LEVEL_COLORS[earlyWarning.sprintMissRisk.riskLevel], fontWeight: 700, letterSpacing: "0.06em" }}>{earlyWarning.sprintMissRisk.riskLevel}</span>}
                  countColor={RISK_LEVEL_COLORS[earlyWarning.sprintMissRisk.riskLevel]}
                >
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
                    {[
                      { label: "Active Tickets",     value: earlyWarning.sprintMissRisk.totalActiveTickets.toLocaleString() },
                      { label: "Avg Daily Velocity", value: `${earlyWarning.sprintMissRisk.avgDailyVelocity}/day` },
                      { label: "Est. Days to Clear", value: earlyWarning.sprintMissRisk.estimatedDaysToClose >= 9999 ? "∞" : earlyWarning.sprintMissRisk.estimatedDaysToClose.toLocaleString(), red: ["HIGH","CRITICAL"].includes(earlyWarning.sprintMissRisk.riskLevel) },
                    ].map(m => (
                      <div key={m.label} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 4, padding: "14px 18px" }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.3)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>{m.label}</div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: m.red ? "#FCA5A5" : "rgba(255,255,255,0.85)", letterSpacing: "-0.02em" }}>{m.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Top Backlog Contributors</div>
                    <ScoreBreakdown
                      dark
                      title="How Factors are scored"
                      intro="Each active ticket earns points from the conditions below; tickets with any points are ranked here, highest first."
                      factors={[
                        { label: "UNASSIGNED — no assignee", points: "+20" },
                        { label: "BLOCKED — On Hold or Blocked status", points: "+15" },
                        { label: "LONG AGING — over 60 days old", points: "+15" },
                        { label: "AGING — over 30 days old", points: "+8" },
                        { label: "HIGH PRIORITY — Critical/Highest, else High", points: "+10 / +5" },
                      ]}
                      footnote="riskLevel: est. days < 90 LOW · < 180 MODERATE · < 365 HIGH · else CRITICAL."
                    />
                  </div>
                  <TicketTable
                    headers={["Ticket", "Priority", "Age", "Assignee", "Status", "Factors"]}
                    rows={earlyWarning.sprintMissRisk.topBacklogTickets.map(t => (
                      <tr key={t.ticketKey} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                        <td style={tdBase}><TicketChip ticketKey={t.ticketKey} onClick={openInvestigation} /></td>
                        <td style={{ ...tdBase, fontSize: 12, fontWeight: 600, color: PC(t.priority) }}>{t.priority ?? "—"}</td>
                        <td style={{ ...tdBase, fontSize: 12, color: "rgba(255,255,255,0.55)" }}>{t.ageingDays ?? "—"}d</td>
                        <td style={{ ...tdBase, fontSize: 12, color: "rgba(255,255,255,0.6)", maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.assignee ?? "—"}</td>
                        <td style={{ ...tdBase, fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{t.status ?? "—"}</td>
                        <td style={{ ...tdBase }}>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {t.riskFactors.map(f => (
                              <span key={f} style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3, background: "rgba(220,38,38,0.12)", color: "#FCA5A5", letterSpacing: "0.05em" }}>{f}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  />
                </SignalRow>

                {/* ── Ownership Gaps ─────────────────────────────────────────── */}
                <SignalRow
                  id="ownership"
                  dot="rgba(255,255,255,0.35)"
                  label="Ownership Gaps"
                  def="Active tickets with nobody assigned — nothing here can move forward until someone takes ownership."
                  summary={`${earlyWarning.ownershipGaps.count} active tickets with no assignee`}
                  count={earlyWarning.ownershipGaps.count}
                  countColor="rgba(255,255,255,0.75)"
                >
                  <TicketTable
                    headers={["Ticket", "Priority", "Age", "Status"]}
                    rows={earlyWarning.ownershipGaps.tickets.map(t => (
                      <tr key={t.ticketKey} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                        <td style={tdBase}><TicketChip ticketKey={t.ticketKey} onClick={openInvestigation} /></td>
                        <td style={{ ...tdBase, fontSize: 12, fontWeight: 600, color: PC(t.priority) }}>{t.priority ?? "—"}</td>
                        <td style={{ ...tdBase, fontSize: 12, color: (t.ageingDays ?? 0) > 60 ? "#FCA5A5" : "rgba(255,255,255,0.55)" }}>{t.ageingDays ?? "—"}d</td>
                        <td style={{ ...tdBase, fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{t.status ?? "—"}</td>
                      </tr>
                    ))}
                  />
                  {earlyWarning.ownershipGaps.count > earlyWarning.ownershipGaps.tickets.length && (
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginTop: 12, fontStyle: "italic" }}>
                      + {earlyWarning.ownershipGaps.count - earlyWarning.ownershipGaps.tickets.length} more unassigned tickets
                    </div>
                  )}
                </SignalRow>

              </div>
            </div>
          </OverviewSection>
          </div>
        );
      })()}

      {/* SECTION 7: Sprint Execution Health */}
      <div id="ov-sprint" style={ANCHOR}>
      <OverviewSection background="page" divider>
      <div style={{
        display: "flex",
        fontFamily: FONT_FAMILY,
        alignItems: "stretch",
        borderRadius: OVERVIEW.cardRadius,
        border: `1px solid ${OVERVIEW.cardBorder}`,
        boxShadow: OVERVIEW.cardShadow,
        overflow: "hidden",
      }}>

        {/* LEFT: Score + clickable driver cards */}
        <div style={{ flex: "0 0 40%", background: "#FFFFFF", padding: 40, display: "flex", flexDirection: "column" }}>
          <SectionHeader
            eyebrow="Sprint Execution Health"
            title="Sprint Execution"
            description="A live score for the current sprint, built from ticket risk and quick-win availability."
            descriptionMaxWidth={340}
          />

          {/* Score + bar */}
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 80, fontWeight: 700, color: scoreColor, letterSpacing: "-0.05em", lineHeight: 1 }}>{closureScore}</div>
            <div style={{ fontSize: 14, color: G.gray400, paddingBottom: 10 }}>/100</div>
          </div>
          <div style={{ height: 6, background: G.gray100, borderRadius: 999, overflow: "hidden", marginBottom: 12 }}>
            <div style={{ width: `${closureScore}%`, height: "100%", background: scoreColor, borderRadius: 999 }} />
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: G.gray800, marginBottom: 8, letterSpacing: "-0.01em" }}>{scoreLabel}</div>
          <div style={{ fontSize: 13, color: G.gray500, lineHeight: 1.75, marginBottom: 36 }}>
            {closureScore >= 80
              ? "Sprint execution is on track. Maintain current pace to close the sprint cleanly."
              : closureScore >= 60
              ? "Sprint shows signs of strain. Address critical and ageing tickets to recover velocity."
              : "Immediate intervention required. Critical issues are accumulating and threatening delivery."}
          </div>

          <ScoreBreakdown
            title="How Sprint Execution Health is calculated"
            intro="Starts at 100. Each risk category subtracts points, up to a cap; quick wins add a small amount back."
            factors={[
              { label: "Critical tickets — 2 pts each", points: "up to −20" },
              { label: "Ageing tickets — 1.5 pts each", points: "up to −20" },
              { label: "Idle tickets — 1.5 pts each", points: "up to −20" },
              { label: "Escalated tickets — 1 pt each", points: "up to −15" },
              { label: "Quick wins available — 1 pt each", points: "up to +15" },
            ]}
            footnote="Score clamped 0–100. 80+ Healthy · 60–79 Needs Attention · below 60 At Risk. The Score Drivers below show exactly which tickets contribute."
            style={{ marginBottom: 32 }}
          />

          {/* Clickable driver cards */}
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: G.gray400, marginBottom: 12 }}>
            Score Drivers — click to inspect tickets
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { key: "critical",  label: "Critical Tickets", count: criticalTickets.length, pts: criticalPenalty, dir: -1 },
              { key: "ageing",    label: "Ageing Tickets",   count: ageingCount,            pts: ageingPenalty,   dir: -1 },
              { key: "idle",      label: "Idle Backlog",     count: idle30Count,            pts: idlePenalty,     dir: -1 },
              { key: "quickwins", label: "Quick Wins",       count: quickWinCount,          pts: quickWinReward,  dir:  1 },
            ].map(d => {
              const active = sprintDriver === d.key;
              return (
                <div key={d.key} onClick={() => setSprintDriver(active ? null : d.key)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 16px", borderRadius: 6, cursor: "pointer",
                    background: active ? G.brandSubtle : "#FAFBFC",
                    border: active ? `1.5px solid ${G.brandBorder}` : "1.5px solid #F1F5F9",
                    transition: "all 0.12s" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? G.brand : G.gray800 }}>{d.label}</span>
                    <span style={{ fontSize: 12, color: G.gray400 }}>{d.count} tickets</span>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: active ? G.brand : G.gray500, letterSpacing: "-0.01em" }}>
                    {d.dir > 0 ? "+" : "−"}{d.pts} pts
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT: Overview or drilldown */}
        <div style={{ flex: 1, background: "#0B223F", padding: 40 }}>
          {sprintDriver === null ? (
            (() => {
              const impactors = [
                ...criticalTickets.map(t => ({ id: t.ticketNo || t.ticket, summary: t.summary, tag: "Critical", impact: 2, detail: `${t.ageingDays ?? 0}d open`, sign: "−" })),
                ...ageingTickets.filter(t => t.overdueDays > 0).map(t => ({ id: t.ticketNo || t.ticket, summary: t.summary, tag: "SLA Breach", impact: 1.5, detail: `${t.overdueDays}d overdue`, sign: "−" })),
                ...idleTickets.map(t => ({ id: t.ticketNo || t.ticket, summary: t.summary, tag: "Idle", impact: 1.5, detail: `${t.idleDays || t.ageingDays}d idle`, sign: "−" })),
                ...quickWinTickets.map(t => ({ id: t.ticketNo || t.ticket, summary: t.summary, tag: "Quick Win", impact: quickWinCount > 0 ? Math.round((quickWinReward / quickWinCount) * 10) / 10 : 0, detail: `Score ${t.winScore}/100`, sign: "+" })),
              ].sort((a, b) => b.impact - a.impact).slice(0, 12);

              const milestones = [70, 80, 90].map(target => {
                const gap = Math.max(0, target - closureScore);
                let actions = [];
                let remaining = gap;
                const crits = Math.min(criticalTickets.length, Math.ceil(remaining / 2));
                if (crits > 0 && remaining > 0) { actions.push(`${crits} critical`); remaining -= crits * 2; }
                const qw = Math.min(quickWinCount, Math.ceil(Math.max(0, remaining) / (quickWinCount > 0 ? quickWinReward / quickWinCount : 1)));
                if (qw > 0 && remaining > 0) { actions.push(`${qw} quick win${qw !== 1 ? "s" : ""}`); remaining -= qw * (quickWinCount > 0 ? quickWinReward / quickWinCount : 0); }
                const aged = Math.min(ageingCount, Math.ceil(Math.max(0, remaining) / 1.5));
                if (aged > 0 && remaining > 0) actions.push(`${aged} ageing`);
                return { target, gap, already: closureScore >= target, actions };
              });

              return (
                <>
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 8 }}>
                    Recovery Roadmap
                  </div>
                  <Description light style={{ marginBottom: 20, maxWidth: 480 }}>
                    Sprint Execution Health milestones, and the fastest combination of critical, quick-win, and ageing tickets that would reach each one.
                  </Description>

                  {/* Score milestones */}
                  <div style={{ display: "flex", gap: 12, marginBottom: 32 }}>
                    {milestones.map(m => (
                      <div key={m.target} style={{ flex: 1, background: m.already ? "rgba(37,99,235,0.15)" : "rgba(255,255,255,0.06)", border: m.already ? `1px solid ${G.brandBorder}` : "1px solid rgba(255,255,255,0.1)", borderRadius: 6, padding: "14px 16px" }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: m.already ? "#93C5FD" : "#FFFFFF", letterSpacing: "-0.02em", marginBottom: 4 }}>{m.target}</div>
                        {m.already ? (
                          <div style={{ fontSize: 11, color: "#93C5FD", fontWeight: 600 }}>Achieved ✓</div>
                        ) : (
                          <>
                            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginBottom: 6 }}>+{m.gap} pts needed</div>
                            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
                              {m.actions.length > 0 ? `Resolve ${m.actions.join(" + ")}` : "On track"}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Prioritized actions */}
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 6 }}>
                    Highest-Impact Tickets — resolve these first
                  </div>
                  <Description light style={{ marginBottom: 12, maxWidth: 480 }}>
                    Every critical, overdue, idle, and quick-win ticket, ranked by how many Sprint Execution Health points it's worth.
                  </Description>
                  {impactors.length === 0 ? (
                    <div style={{ padding: "32px 0", textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: 13 }}>No action items — sprint is clean</div>
                  ) : (
                    <div style={{ borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "130px 1fr 90px 60px", gap: 12, padding: "8px 14px", background: "rgba(255,255,255,0.05)", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                        {["Ticket", "Summary", "Category", "Impact"].map(h => (
                          <div key={h} style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.28)" }}>{h}</div>
                        ))}
                      </div>
                      {impactors.map((t, i) => (
                        <div key={i}
                          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? "rgba(255,255,255,0.03)" : "transparent"; }}
                          style={{ display: "grid", gridTemplateColumns: "130px 1fr 90px 60px", gap: 12, alignItems: "center", padding: "11px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)", background: i % 2 === 0 ? "rgba(255,255,255,0.03)" : "transparent", transition: "background 0.12s" }}>
                          <TicketChip ticketKey={t.id} onClick={openInvestigation} />
                          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.summary || t.detail}</span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.4)" }}>{t.tag}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: t.sign === "+" ? "#93C5FD" : "rgba(255,255,255,0.75)" }}>{t.sign}{t.impact}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()
          ) : (
            (() => {
              const driverConfig = {
                critical:  { label: "Critical Tickets", tickets: criticalTickets,  pts: criticalPenalty,  color: "rgba(255,255,255,0.6)", sign: "−", getDetail: t => `${t.priority || "—"} · ${t.ageingDays ?? 0}d open` },
                ageing:    { label: "Ageing Tickets",   tickets: ageingTickets,    pts: ageingPenalty,    color: "rgba(255,255,255,0.6)", sign: "−", getDetail: t => t.overdueDays > 0 ? `${t.overdueDays}d overdue` : `${t.ageingDays}d · at risk` },
                idle:      { label: "Idle Backlog",     tickets: idleTickets,      pts: idlePenalty,      color: "rgba(255,255,255,0.6)", sign: "−", getDetail: t => `${t.idleDays || t.ageingDays}d idle` },
                quickwins: { label: "Quick Wins",       tickets: quickWinTickets,  pts: quickWinReward,   color: "#93C5FD",              sign: "+", getDetail: t => `Win score ${t.winScore}/100` },
              }[sprintDriver];
              return (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 8 }}>
                        Score Drilldown
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#FFFFFF", letterSpacing: "-0.01em" }}>{driverConfig.label}</div>
                      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>
                        {driverConfig.tickets.length} tickets · {driverConfig.sign}{driverConfig.pts} pts impact
                      </div>
                    </div>
                    <button onClick={() => setSprintDriver(null)}
                      style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 4, padding: "6px 14px", cursor: "pointer", fontFamily: FONT_FAMILY, flexShrink: 0 }}>
                      ← Overview
                    </button>
                  </div>
                  {driverConfig.tickets.length === 0 ? (
                    <div style={{ padding: "48px 0", textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: 13 }}>No tickets in this category</div>
                  ) : (
                    <div style={{ borderRadius: 4, overflow: "hidden" }}>
                      {/* Column headers */}
                      <div style={{ display: "grid", gridTemplateColumns: "140px 1fr auto", gap: 16, padding: "9px 16px", background: "rgba(255,255,255,0.05)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                        {["Ticket", "Summary", "Detail"].map(h => (
                          <div key={h} style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)" }}>{h}</div>
                        ))}
                      </div>
                      {driverConfig.tickets.slice(0, 14).map((t, i) => (
                        <div key={i}
                          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? "rgba(255,255,255,0.03)" : "transparent"; }}
                          style={{ display: "grid", gridTemplateColumns: "140px 1fr auto", gap: 16, alignItems: "center", padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)", background: i % 2 === 0 ? "rgba(255,255,255,0.03)" : "transparent", transition: "background 0.12s" }}>
                          <TicketChip ticketKey={t.ticketNo || t.ticket} onClick={openInvestigation} />
                          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {t.summary || "No summary"}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: driverConfig.color, whiteSpace: "nowrap" }}>
                            {driverConfig.getDetail(t)}
                          </span>
                        </div>
                      ))}
                      {driverConfig.tickets.length > 14 && (
                        <div style={{ padding: "10px 16px", fontSize: 12, color: "rgba(255,255,255,0.25)", textAlign: "center", background: "rgba(255,255,255,0.03)" }}>
                          +{driverConfig.tickets.length - 14} more tickets
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()
          )}
        </div>
      </div>
      </OverviewSection>
      </div>

      {/* SECTION 8: AI Intelligence */}
      <div id="ov-assistant" style={ANCHOR}>
      <OverviewSection background="page" divider>
      <div style={{ borderRadius: OVERVIEW.cardRadius, border: `1px solid ${OVERVIEW.cardBorder}`, boxShadow: OVERVIEW.cardShadow, overflow: "hidden" }}>
      <div style={{ background: "#071A2F", padding: "48px 40px" }}>

        {/* Header */}
        <SectionHeader
          light
          eyebrow="AI Intelligence"
          title="Sprint Assistant"
          description="Ask questions about your sprint, team performance, ticket health, and delivery risks."
          actions={
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, padding: "6px 14px", flexShrink: 0 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: "rgba(255,255,255,0.2)" }} />
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", fontWeight: 600, letterSpacing: "0.04em" }}>AI Integration Coming Soon</span>
            </div>
          }
        />

        {/* Main 2-column layout */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.8fr", gap: 24, alignItems: "start" }}>

          {/* LEFT — Suggested prompts */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {[
              {
                category: "Sprint Health",
                prompts: [
                  "Why is my sprint score dropping this week?",
                  "What's the biggest risk to delivery right now?",
                ],
              },
              {
                category: "Team Performance",
                prompts: [
                  "Who has the highest open ticket load?",
                  "Which developer is closest to their SLA limit?",
                ],
              },
              {
                category: "Ticket Analysis",
                prompts: [
                  "Which tickets are most at risk of breaching SLA?",
                  "Show me unresolved critical tickets older than 7 days.",
                ],
              },
              {
                category: "Action Plan",
                prompts: [
                  "What should I resolve first to recover sprint score?",
                  "Give me the top 5 fastest wins available today.",
                ],
              },
            ].map(group => (
              <div key={group.category}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", marginBottom: 8 }}>
                  {group.category}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {group.prompts.map(p => (
                    <div key={p} onClick={() => setAiPrompt(p)}
                      onMouseEnter={e => { if (aiPrompt !== p) { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "rgba(255,255,255,0.72)"; } }}
                      onMouseLeave={e => { if (aiPrompt !== p) { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.color = "rgba(255,255,255,0.5)"; } }}
                      style={{ padding: "10px 14px", background: aiPrompt === p ? "rgba(37,99,235,0.2)" : "rgba(255,255,255,0.04)", border: aiPrompt === p ? "1px solid rgba(37,99,235,0.5)" : "1px solid rgba(255,255,255,0.07)", borderRadius: 6, cursor: "pointer", fontSize: 13, color: aiPrompt === p ? "#93C5FD" : "rgba(255,255,255,0.5)", lineHeight: 1.45, transition: "all 0.1s" }}>
                      {p}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT — Chat interface */}
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, overflow: "hidden", display: "flex", flexDirection: "column" }}>

            {/* Response area */}
            <div style={{ flex: 1, minHeight: 360, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 40px" }}>
              {aiPrompt ? (
                <div style={{ width: "100%" }}>
                  {/* User message bubble */}
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 24 }}>
                    <div style={{ background: "#2563EB", borderRadius: "12px 12px 2px 12px", padding: "12px 18px", maxWidth: "80%", fontSize: 13, color: "#FFFFFF", lineHeight: 1.55 }}>
                      {aiPrompt}
                    </div>
                  </div>
                  {/* Assistant placeholder */}
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(37,99,235,0.2)", border: "1px solid rgba(37,99,235,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, color: "#93C5FD", fontWeight: 700 }}>
                      AI
                    </div>
                    <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px 12px 12px 2px", padding: "14px 18px", fontSize: 13, color: "rgba(255,255,255,0.35)", lineHeight: 1.6, fontStyle: "italic" }}>
                      AI integration is coming soon. Once connected, your Sprint Assistant will analyse live sprint data and respond here.
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center" }}>
                  <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(37,99,235,0.12)", border: "1px solid rgba(37,99,235,0.25)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 18, color: "#93C5FD", fontWeight: 700 }}>
                    AI
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>Ready to assist</div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.25)", lineHeight: 1.6, maxWidth: 320 }}>
                    Select a suggested prompt on the left or type your own question below.
                  </div>
                </div>
              )}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: "rgba(255,255,255,0.07)" }} />

            {/* Input bar */}
            <div style={{ padding: "16px 20px", display: "flex", gap: 12, alignItems: "flex-end" }}>
              <textarea
                value={aiPrompt}
                onChange={e => setAiPrompt(e.target.value)}
                placeholder="Ask about your sprint, team, or tickets..."
                rows={2}
                style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, padding: "10px 14px", fontSize: 13, fontFamily: FONT_FAMILY, resize: "none", outline: "none", lineHeight: 1.5, color: "rgba(255,255,255,0.8)" }}
              />
              <button
                disabled
                style={{ padding: "10px 20px", background: aiPrompt.trim() ? "#2563EB" : "rgba(255,255,255,0.06)", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, color: aiPrompt.trim() ? "#FFFFFF" : "rgba(255,255,255,0.2)", fontFamily: FONT_FAMILY, cursor: "not-allowed", flexShrink: 0, transition: "all 0.12s" }}>
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
      </OverviewSection>
      </div>

      </div>
    </div>

    <InvestigationDrawer
      ticketKey={investigationTicket}
      data={investigationData}
      prs={investigationPRs}
      loading={investigationLoading}
      onClose={() => setInvestigationTicket(null)}
    />
    </>
  );
}


// ─── Commitment Charts ────────────────────────────────────────────────────────
function CommitBarChart({ data }) {
  if (!data || data.length === 0) {
    return <div style={{ textAlign: "center", color: "#9CA3AF", padding: 20, fontSize: 12 }}>No trend data yet</div>;
  }
  const maxVal = Math.max(...data.flatMap(d => [d.committed, d.completed]), 1);
  const W = 500;
  const plotH = 100;
  const padL = 30;
  const padB = 20;
  const plotW = W - padL - 10;
  const n = Math.max(data.length - 1, 1);
  const committedPts = data.map((d, i) => ({ x: padL + (i / n) * plotW, y: (1 - d.committed / maxVal) * plotH, date: d.date }));
  const completedPts = data.map((d, i) => ({ x: padL + (i / n) * plotW, y: (1 - d.completed / maxVal) * plotH, date: d.date }));
  const commitPath = committedPts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const completePath = completedPts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${plotH + padB}`} width="100%" style={{ overflow: "visible", display: "block" }}>
      <line x1={padL} y1={0} x2={padL} y2={plotH} stroke="#E5E7EB" />
      <line x1={padL} y1={plotH} x2={W - 10} y2={plotH} stroke="#E5E7EB" />
      <text x={padL - 4} y={8} textAnchor="end" fontSize="11" fill="#9CA3AF">{maxVal}</text>
      <text x={padL - 4} y={plotH} textAnchor="end" fontSize="11" fill="#9CA3AF">0</text>
      <path d={commitPath} fill="none" stroke="#3B82F6" strokeWidth="1.5" strokeLinejoin="round" />
      {committedPts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={2.5} fill="#3B82F6" />)}
      <path d={completePath} fill="none" stroke="#16A34A" strokeWidth="1.5" strokeLinejoin="round" />
      {completedPts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={2.5} fill="#16A34A" />)}
      {committedPts.filter((_, i) => i % 2 === 0 || i === committedPts.length - 1).map((p, i) => (
        <text key={i} x={p.x} y={plotH + padB - 2} textAnchor="middle" fontSize="10" fill="#9CA3AF">
          {p.date ? p.date.slice(5) : ""}
        </text>
      ))}
    </svg>
  );
}

function CommitLineChart({ data }) {
  if (!data || data.length < 2) {
    return <div style={{ textAlign: "center", color: "#9CA3AF", padding: 20, fontSize: 12 }}>Not enough data yet</div>;
  }
  const maxVal = Math.max(...data.map(d => d.carryForward), 1);
  const W = 500;
  const plotH = 80;
  const padL = 30;
  const padB = 20;
  const plotW = W - padL - 10;
  const pts = data.map((d, i) => ({
    x: padL + (i / (data.length - 1)) * plotW,
    y: (1 - d.carryForward / maxVal) * plotH,
    date: d.date,
  }));
  const pathD = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${plotH + padB}`} width="100%" style={{ overflow: "visible", display: "block" }}>
      <line x1={padL} y1={0} x2={padL} y2={plotH} stroke="#E5E7EB" />
      <line x1={padL} y1={plotH} x2={W - 10} y2={plotH} stroke="#E5E7EB" />
      <text x={padL - 4} y={8} textAnchor="end" fontSize="11" fill="#9CA3AF">{maxVal}</text>
      <text x={padL - 4} y={plotH} textAnchor="end" fontSize="11" fill="#9CA3AF">0</text>
      <path d={pathD} fill="none" stroke="#D97706" strokeWidth="1.5" strokeLinejoin="round" />
      {pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={2.5} fill="#D97706" />)}
      {pts.filter((_, i) => i % 2 === 0 || i === pts.length - 1).map((p, i) => (
        <text key={i} x={p.x} y={plotH + padB - 2} textAnchor="middle" fontSize="10" fill="#9CA3AF">
          {p.date ? p.date.slice(5) : ""}
        </text>
      ))}
    </svg>
  );
}
